import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Navigation, CheckCircle, Check, Clock, GripVertical, WifiOff } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { QuoteVisitCard, VisitSheet, IntakeSheet, iso } from './BidderHome';
import InspectorHome from './InspectorHome';
import { RouteMap } from '../components/RouteBoard';
import { workLine } from '../utils/crewWork';
import { useReadOnly } from '../hooks/useReadOnly';
import { useBidderOffline } from '../hooks/useBidderOffline';
import { PlanKept, PlanProblems, PlanRowTime, PlanHelp, PlanAfterEnd, PlanWaits, clockToneClass } from '../components/DayPlanNotes';
import TodoSheet from '../components/TodoSheet';
import AutoStartStrip from '../components/AutoStartStrip';
import FoldCard from '../components/FoldCard';
import { useAutoStop } from '../hooks/useAutoStop';
import { GeoStop } from '../utils/geoAutoStop';
import { clock12, clockWords, dayChangedWords, promiseOfHours, startPlace } from '../utils/dayPlan';

/**
 * Field — the owner's own day, in one column.
 *
 * His words (Sep 18 2026): "make a profile for Michael that combines bidder,
 * inspector and employee all in one, so it shows all my stops I have to do —
 * then I just switch from that to admin when I need to."
 *
 * Sections:
 *   Today's stops  — his mowing stops (GET /jobs?crew_member_id) and his quote
 *                    visits (GET /quotes/visits) in one list and on one map, by
 *                    time (Sep 24 2026: "include quotes in the stops of the day")
 *   Lawns to inspect — the inspector's week
 * The hat switcher in the sidebar offers the admin side back.
 */

/**
 * Two lists already in order, one list by time. Mowing stops keep route order
 * and quote visits keep theirs; a stop with no time goes after the timed ones
 * of the other list rather than jumping ahead of them.
 */
// A quote visit sorts on its HOURS (`sort_time`, from the server): a "by 9 AM"
// visit has no time_start of its own and used to land after every afternoon stop.
function byTime<T extends { time_start?: string | null; sort_time?: string | null }>(a: T[], b: T[]): T[] {
  const t = (x: T) => { const at = x.sort_time || x.time_start; return at ? String(at).slice(0, 5) : '99:99'; };
  const out: T[] = [];
  let i = 0, j = 0;
  while (i < a.length && j < b.length) out.push(t(b[j]) < t(a[i]) ? b[j++] : a[i++]);
  return out.concat(a.slice(i), b.slice(j));
}
/**
 * The day in the order he picked with Optimize (jobs.day_order for that date),
 * anything added since after it by time. No saved order: by time, as before.
 */
function inDayOrder<T extends { day_order?: number | null; day_order_date?: string | null; time_start?: string | null }>(all: T[], date: string): T[] {
  const set = (x: T) => x.day_order != null && String(x.day_order_date || '').slice(0, 10) === date;
  const planned = all.filter(set).sort((a, b) => Number(a.day_order) - Number(b.day_order));
  if (!planned.length) return all;
  return planned.concat(byTime(all.filter(x => !set(x)), []));
}
// How often the page looks at the day again on its own.
const FIVE_MIN = 5 * 60 * 1000;
const stopAddr = (j: any) => j.address || j.stop_address || '';
const mapsLink = (j: any) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent([stopAddr(j), j.city].filter(Boolean).join(', '))}`;
// What the stop is, the way the crew side says it: "Mowing", with anything
// else on the visit as additional (utils/crewWork; owner, Sep 30 2026).
const label = (j: any) => (j.service_type === 'todo' ? 'To-do' : workLine(j));

/**
 * The rest of the day: one group per route the office is not on, plus
 * everything off a route in one group last (server/utils/dayScope.groupDay).
 */
type DayGroup = {
  key: string;
  route_id: number | null;
  route_name: string | null;
  route_color: string | null;
  crew: string[];          // who is on it: job_crew, else the crew sent that day, else the lone assignee
  job_ids: number[];
  total: number;
  done: number;
  rows?: any[];            // filled in on the page from the jobs it names
};

/**
 * One route's stops, folded to a line: "Monday Week B · Ryan, James · 3/16
 * done". Read only — this is somebody else's route, and reordering it belongs
 * on the Routes page, where a change mid-route warns first. A stop opens its
 * own job page.
 */
function DayGroupCard({ g }: { g: DayGroup }) {
  const rows = g.rows || [];
  const left = g.total - g.done;
  return (
    <FoldCard storageKey={`field_day_group_${g.key}`}
      icon={<span className="h-3 w-3 rounded-full inline-block flex-shrink-0" style={{ background: g.route_color || '#d1d5db' }} />}
      title={g.route_name || 'Not on a route'}
      summary={`${g.crew.length ? g.crew.join(', ') : 'nobody on it yet'} · ${left ? `${left} to do` : 'all done'} of ${g.total}`}>
      <ul className="divide-y divide-gray-100 -mx-1">
        {rows.map((j, i) => (
          <li key={j.id}>
            <Link to={`/jobs/${j.id}`} className="flex items-baseline gap-2 px-1 py-2">
              <span className="w-5 text-right text-xs text-gray-400 flex-shrink-0">{i + 1}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-gray-900 truncate">
                  {j.service_type === 'todo' ? j.title || 'To-do' : j.customer_name}
                </span>
                {stopAddr(j) && <span className="block text-xs text-gray-500 truncate">{stopAddr(j)}{j.city ? `, ${j.city}` : ''}</span>}
              </span>
              <span className="text-xs flex-shrink-0">
                {j.status === 'complete' ? <span className="text-green-700 font-semibold">Done</span>
                  : j.status === 'in_progress' ? <span className="text-amber-700 font-semibold">Started</span>
                  : <span className="text-gray-500">{j.time_start ? clock12(j.time_start) : 'Anytime'}</span>}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </FoldCard>
  );
}

/**
 * The page at /field: the title line, then the day. Since Nov 2 2026 the same
 * block sits on the one morning page (pages/Today) under its own header, so
 * the day itself is `FieldDay` and this page only adds the title.
 */
export default function FieldHome() {
  return <FieldDay />;
}

/**
 * The owner's field day as one block — his stops and quote visits, then the
 * lawns to inspect. `embedded` leaves the page title off (the block sits
 * under a header of its own on pages/Today); nothing else changes.
 */
export function FieldDay({ embedded = false }: { embedded?: boolean }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  // "Today" is held, not read once: the page is left open overnight, and at
  // 12:01 AM it must not go on showing yesterday's stops as today's (below).
  const [today, setToday] = useState(() => iso(new Date()));
  // Any day, not just today (owner, Sep 27 2026: "Monday has quotes, let me put them in a route").
  const [day, setDay] = useState(today);
  const isToday = day === today;
  // Another day is another plan: the open preview closes and the Start at box
  // empties. It used to keep whatever was typed — a 6:00 AM for tomorrow came
  // back to today with him, and at 1 PM the plan said "leave 6:00 AM".
  const goDay = (next: string) => {
    dropReplan();
    setDay(next); setPlan(null); setStartAt('');
  };
  const shiftDay = (n: number) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); goDay(iso(d)); };
  // ── The page keeps itself current ──────────────────────────────────────────
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work"; "you have time to fully fix it — make this a fully functioning
  // feature". He opens Field at 6 AM and looks again at 1 PM, phone just
  // unlocked: the list was the one loaded at 6 (a customer who booked online
  // at 9 was not on it), the times were the morning's, and after midnight the
  // page showed yesterday's stops under today's date. Nothing in this app
  // reloads on its own (App.tsx turns that off for every page), so this page
  // does it itself: when he comes back to it (the tab shows again, the window
  // gets focus) and every five minutes while it is open, both lists are
  // fetched again and the day's clock is asked again (`bucket` is in its key);
  // and when the date rolls over while he is on "today", the page follows it.
  const [bucket, setBucket] = useState(() => Math.floor(Date.now() / FIVE_MIN));
  const live = useRef({ day, today, goDay });
  live.current = { day, today, goDay };
  useEffect(() => {
    let seen = Math.floor(Date.now() / FIVE_MIN), fetched = Date.now();
    const look = (back: boolean) => {
      const now = Date.now();
      const date = iso(new Date(now));
      if (date !== live.current.today) {
        const was = live.current.today;
        live.current.today = date;
        setToday(date);
        if (live.current.day === was) live.current.goDay(date);
      }
      // A new five minutes, or he has just come back — once, not twice: a phone
      // reports both "showing again" and "has focus" for the same unlock.
      const b = Math.floor(now / FIVE_MIN);
      if (b !== seen || (back && now - fetched > 5000)) {
        seen = b; fetched = now;
        setBucket(b);
        qc.invalidateQueries('field-stops'); qc.invalidateQueries('bid-visits'); qc.invalidateQueries('day-clock');
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') look(true); };
    const onFocus = () => look(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    const timer = setInterval(() => look(false), 30_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      clearInterval(timer);
    };
  }, [qc]);
  const dayWords = new Date(`${day}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  const cm = (user as any)?.crewMemberId || null;
  // The office side: admin and the read-only viewer. A crew login never gets
  // here (App.tsx's admin shell), and the server refuses it the whole day
  // anyway — this only decides what the page asks for.
  const office = user?.role === 'admin' || user?.role === 'viewer';
  const [visitSheet, setVisitSheet] = useState<{ date?: string; lead?: any } | null>(null);
  // A to-do on the day — with a client and one of their addresses when it is for someone
  // (components/TodoSheet, services/todos; owner, Oct 26 2026). Me by default; a person puts it on THEIR day.
  const [todoOpen, setTodoOpen] = useState(false);
  const [intake, setIntake] = useState<any | null>(null);

  // ── No signal ──────────────────────────────────────────────────────────────
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work"; "you have time to fully fix it — make this a fully functioning
  // feature". A visit card's "On the way", "Here", Done and Skip save on the
  // phone in a dead spot and say "sends when you are back online" — but the
  // only thing that ever sent them lived on the Bids → Today tab. Tapped here,
  // the "Here" text sat on the phone until someone opened that tab. The queue
  // is sent from this page now (hooks/useBidderOffline): when signal comes
  // back, when he returns to the page, and once a minute while something waits.
  const { isOnline, pending, flush } = useBidderOffline(() => {
    qc.invalidateQueries('bid-visits'); qc.invalidateQueries('field-stops'); qc.invalidateQueries('day-clock');
  });

  // ── A refresh that fails is an OLD page, not a broken one ──────────────────
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work". The page now looks again on its own every five minutes — and in a
  // dead spot one failed look used to print "Could not load your stops" over a
  // list that was still the whole day, take Optimize and every drag handle
  // away, and blank the arrival times and the call/text links. A failed look
  // that still has the last good answer keeps showing it, with one amber line
  // saying how old it is. "Could not load" is only for a page with nothing to
  // show.
  //
  // ── The whole day, not just his own stops ─────────────────────────────────
  // The office asks with &all_crews=1 (server/utils/dayScope): every stop of
  // the day comes back, each stamped `mine`, with the rest grouped by route
  // in `day_groups`. Before this the page asked the crew app's own question —
  // "what is this person on?" — so on a day the crew is out and he is not
  // (Monday Week B is Ryan and James's) it answered nothing, and the page he
  // plans the day on read "Nothing on your list today" over 16 stops and five
  // loose extras. His own stops are still the list below: the only one he
  // drags, Optimizes and is put on the clock. The rest is there to read.
  const stopsQ = useQuery(['field-stops', day, cm, office ? 'all' : 'mine'],
    () => api.get('/jobs', { params: { date: day, ...(cm ? { crew_member_id: cm } : {}), ...(office ? { all_crews: 1 } : {}) } })
      .then(r => r.data as { jobs: any[]; day_groups?: DayGroup[] }),
    { enabled: !!cm || office, retry: false });
  const { isLoading } = stopsQ;
  const data = stopsQ.data?.jobs;
  const isError = stopsQ.isError && !stopsQ.data;
  // Quote visits have their own section above; a canceled stop is nobody's —
  // and neither is a skipped one. A mow he had skipped stayed on this list with
  // Open / Time / Done under it (Done answered "that visit was skipped"), kept
  // its stop number, and went into Optimize as a stop already "started".
  // Skipped is off the day, the same as cancelled (the server's /day-order
  // drops it too; a skipped quote visit never reaches this page).
  const offTheDay = (st: any) => ['canceled', 'cancelled', 'skipped'].includes(String(st || ''));
  // `mine` is false only on a whole-day answer, for a stop somebody else is on
  // (undefined everywhere else, so a crew login's list is unchanged).
  const onTheDay = (j: any) => (j.service_type || 'mowing') !== 'estimate' && !offTheDay(j.status);
  const mowing = (data || []).filter(j => onTheDay(j) && j.mine !== false)
    .map(j => ({ ...j, _quote: false }));
  // The rest of the day, by the job id its group names.
  const rowById = new Map<number, any>((data || []).map(j => [Number(j.id), j]));
  const dayGroups: DayGroup[] = (stopsQ.data?.day_groups || [])
    .map(g => ({ ...g, rows: g.job_ids.map(id => rowById.get(Number(id))).filter(Boolean) }))
    .filter(g => g.rows!.length > 0);
  const elsewhere = dayGroups.reduce((n, g) => n + g.total, 0);
  const elsewhereOpen = dayGroups.reduce((n, g) => n + (g.total - g.done), 0);
  // Quote visits — the bidder's own query (and cache key).
  const visitsQ = useQuery(['bid-visits', day, day],
    () => api.get('/quotes/visits', { params: { from: day, to: day } }).then(r => r.data.visits as any[]),
    { retry: false });
  const { data: visitData, isLoading: visitsLoading, refetch: refetchVisits } = visitsQ;
  const visitsError = visitsQ.isError && !visitsQ.data;
  // The day is two lists — his stops and the quote visits. Until BOTH are in
  // there is no Optimize and no dragging: a save made on half the day numbered
  // only that half and left the rest with their old stop numbers.
  const listsReady = ((!cm && !office) || (!isLoading && !isError)) && !visitsLoading && !visitsError;
  const quoteStops = (visitData || []).filter(v => !offTheDay(v.status)).map(v => ({ ...v, _quote: true }));
  // A drag shows at once; the saved order takes over when the list reloads.
  const [dragOrder, setDragOrder] = useState<number[] | null>(null);
  const baseStops = inDayOrder<any>(byTime<any>(mowing, quoteStops), day);
  const stops = dragOrder
    ? baseStops.slice().sort((a, b) => (dragOrder.indexOf(a.id) + 1 || 1e9) - (dragOrder.indexOf(b.id) + 1 || 1e9))
    : baseStops;
  const ordered = stops.some(j => j.day_order != null && String(j.day_order_date || '').slice(0, 10) === day);
  const open = stops.filter(j => j.status !== 'complete');

  // ── The day's clock — one answer, asked again all day ──────────────────────
  //
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work"; "you have time to fully fix it — make this a fully functioning
  // feature".
  //
  // The day goes on the clock in the order shown: drive time + visit time
  // (owner, Sep 28 2026: "get the times to assume ETAs"). It is a preview —
  // nothing is written until Optimize or a drag.
  //
  // That used to stop the moment he saved an order: from then on the cards read
  // the arrival times stored on the jobs that morning and never asked again.
  // At 1:05 PM, four stops done, every card still showed the morning's times,
  // an 11:00 AM visit he had not got to read a plain "11:00 AM", a visit added
  // at 9 sat at the bottom with no time, and a stop carried over from
  // yesterday showed yesterday's arrival. So a saved day asks the SAME clock:
  // his order, put on the clock from where he is at the time it is, and the
  // cards, the summary line and the problem paragraphs all read that one
  // answer. Nothing here works out lateness for itself any more.
  //
  // The key says when to ask again: every five minutes today (`bucket`), and
  // whenever any stop's status, time or hours change — here, on the visit's
  // own page, or from the office.
  const clockKey = stops.map(j => `${j.id}:${j.status}@${j.time_start || ''}${Number(j.time_is_firm) ? '!' : ''}${Number(j.time_anytime) ? '~' : ''}${j._quote ? `[${j.hours?.exact ?? ''},${j.hours?.earliest ?? ''},${j.hours?.latest ?? ''}]` : ''}`).join(',');
  const clockQ = useQuery(['day-clock', day, isToday ? bucket : 0, clockKey],
    () => api.post('/jobs/day-order', { date: day, job_ids: stops.map(j => j.id), manual: true }).then(r => r.data),
    { enabled: !readOnly && listsReady && !dragOrder && stops.length > 0, staleTime: 60_000, retry: false, keepPreviousData: true });
  // While the next answer is on its way the last one stays up (no flash of
  // empty times every five minutes) — except straight after a save, when the
  // save's own answer IS the new clock and the old one is for another order.
  const [applied, setApplied] = useState<{ ids: string; data: any } | null>(null);
  const idsNow = stops.map(j => j.id).join(',');
  const fresh = clockQ.data && !clockQ.isPreviousData && clockQ.data.date === day ? clockQ.data : null;
  useEffect(() => { if (fresh && applied) setApplied(null); }, [fresh, applied]);
  // The last good answer for this day and these stops: what the cards go on
  // showing when the newest ask fails (no signal).
  const lastClock = useRef<{ day: string; ids: string; data: any; at: number } | null>(null);
  if (fresh) lastClock.current = { day, ids: idsNow, data: fresh, at: clockQ.dataUpdatedAt || Date.now() };
  const kept = clockQ.isError && lastClock.current && lastClock.current.day === day && lastClock.current.ids === idsNow ? lastClock.current : null;
  const clock: any = fresh
    || (applied && applied.ids === idsNow && applied.data.date === day ? applied.data : null)
    || (clockQ.data && clockQ.data.date === day ? clockQ.data : null)
    || (kept ? kept.data : null);
  // What is on the screen from an earlier look, and since when.
  const staleSince = [
    stopsQ.isError && stopsQ.data ? stopsQ.dataUpdatedAt : 0,
    visitsQ.isError && visitsQ.data ? visitsQ.dataUpdatedAt : 0,
    kept && !fresh ? kept.at : 0,
  ].filter(Boolean);
  const staleWords = staleSince.length
    ? new Date(Math.min(...staleSince)).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
  const lookAgain = () => { qc.invalidateQueries('field-stops'); qc.invalidateQueries('bid-visits'); qc.invalidateQueries('day-clock'); };
  const rowOf: Record<number, any> = {};
  for (const s of clock?.stops || []) rowOf[s.job_id] = s;
  // What the stop is promised: a quote visit's hours (exact, or between two
  // times — the rule the planner reads); any other job, its must-start time.
  const promiseOf = (j: any) => (j._quote
    ? promiseOfHours(j.hours)
    : { must_start: Number(j.time_is_firm) && j.time_start ? String(j.time_start).slice(0, 5) : null, window: null });
  // When a stop is, on its card, in the clock's own words and colours — the
  // same line the plan panel shows for it: "⏰ 10:00 AM" (kept, dark),
  // "⏰ 7–10 AM · there ~9:05 AM", "~1:34 PM" (no set time), "⏰ 2:00 PM ·
  // about 3 min behind" (amber), "⏰ 4:00 PM · there ~4:24 PM" (late, red),
  // "was 11:00 AM · now ~1:54 PM" (gone by). Red used to mean two things here:
  // any must-start time was red, kept or not. A finished stop just says Done.
  const whenNote = (j: any): React.ReactNode => {
    if (j.status === 'complete') return 'Done';
    const row = rowOf[j.id];
    if (!row || row.fixed) return null;
    const w = clockWords(row);
    return <span className={clockToneClass(w.tone)}>{w.text}</span>;
  };
  // Until the clock answers (or for a view-only login, which cannot ask it): the stop's own time, no arrival.
  const ownTime = (j: any): React.ReactNode => (Number(j.time_is_firm) && j.time_start
    ? <span className={clockToneClass('kept')}>⏰ {clock12(j.time_start)}</span>
    : j.time_anytime || !j.time_start ? 'Anytime' : clock12(j.time_start));
  // "next: Priya Nair" on a day under way; else "from Mario Delgado's · leave now" / "leave 8:00 AM".
  const whereNow = (c: any): string => (c?.under_way && c.next?.name
    ? `next: ${c.next.todo ? `"${c.next.name}"` : c.next.name}`
    : `${c?.from_job_id != null && startPlace(c) ? `from ${startPlace(c)} · ` : ''}${c?.leave_now ? 'leave now' : `leave ${clock12(c?.start)}`}`);
  // What has turned up since he saved this order — the one line that says "Re-plan".
  const changedWords = ordered ? dayChangedWords(clock?.changed) : '';
  // The map: pins numbered like the "Stop N" cards below, the next stop to do
  // pulsing. A stop with no GPS keeps its number and just isn't pinned.
  const mapStops = stops.map(j => ({
    id: j.id, status: j.status,
    customer_name: j._quote ? `Quote · ${j.customer_name || ''}` : j.service_type === 'todo' ? `To-do · ${j.title || ''}` : j.customer_name,
    address: stopAddr(j),
    property_label: j.property_label,
    lat: j.lat ?? j.stop_lat ?? null, lng: j.lng ?? j.stop_lng ?? null,
    // What to do there, for the pin's label — not for a quote visit or a to-do, whose name says it.
    ...(j._quote || j.service_type === 'todo' ? {} : {
      service_type: j.service_type ?? null, title: j.title ?? null, route_id: j.route_id ?? null,
      work_main: j.work_main ?? null, work_additional: j.work_additional ?? null,
    }),
  })).map(s => ({ ...s, lat: s.lat != null ? Number(s.lat) : null, lng: s.lng != null ? Number(s.lng) : null }));
  const unpinned = mapStops.filter(s => s.lat == null || s.lng == null).length;
  const nextId = open.find(j => j.status !== 'skipped')?.id ?? null;

  // Optimize: a preview first, then "Use this order". Must-start times are kept.
  //
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work"; "you have time to fully fix it — make this a fully functioning
  // feature". The order he approves is the order that is saved: "Use this
  // order" sends the plan on his screen back (`shown` — its stops in its
  // order, its start, where it left from) and the server only puts that on the
  // clock. It used to send the stop ids and whatever was in the Start at box
  // and get a second, different plan made on a later clock.
  const [plan, setPlan] = useState<any | null>(null);
  const [startAt, setStartAt] = useState('');
  const asked = useRef(0);   // which preview was asked for last — an older answer never replaces a newer one
  const replanTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The panel is closing (saved, cancelled, a drag, another day): a re-plan
  // still waiting on the time wheel, or one whose answer is still on its way,
  // must not open it again.
  const dropReplan = () => {
    if (replanTimer.current) { clearTimeout(replanTimer.current); replanTimer.current = null; }
    asked.current++;
  };
  useEffect(() => () => { if (replanTimer.current) clearTimeout(replanTimer.current); }, []);
  // Undo for "Use this order". It replaces whatever order was there — one he
  // dragged by hand included — and there was no way back. The save answers
  // with what it replaced (`before`): that order and its start are saved
  // again, or, if he was in time order, the order is cleared.
  const undo = useMutation((b: { date: string; before: any }) => (b.before?.ordered && b.before.job_ids?.length
    ? api.post('/jobs/day-order', { date: b.date, job_ids: b.before.job_ids, manual: true, apply: true, ...(b.before.start ? { start: b.before.start } : {}) })
    : api.delete('/jobs/day-order', { params: { date: b.date } })).then(r => r.data), {
    onSuccess: async (_d: any, b) => {
      toast.success(b.before?.ordered ? 'Back to the order you had' : 'Back to time order');
      await Promise.all([qc.invalidateQueries('field-stops'), qc.invalidateQueries('bid-visits'), qc.invalidateQueries('day-clock')]);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not undo that'); },
  });
  const optimize = useMutation((body: { apply?: boolean; manual?: boolean; job_ids?: number[]; start?: string | null; shown?: any; lateBefore?: number[] | null }) => {
    if (body.shown) {
      const p = body.shown;
      return api.post('/jobs/day-order', {
        date: p.date || day, job_ids: p.stops.map((s: any) => s.job_id), apply: true, manual: true,
        as_shown: true, start: p.asked_start, from_job_id: p.from_job_id ?? null,
      }).then(r => r.data);
    }
    const start = body.start !== undefined ? body.start : startAt;
    const seq = body.apply ? 0 : ++asked.current;
    return api.post('/jobs/day-order', { date: day, job_ids: body.job_ids || stops.map(j => j.id), apply: !!body.apply, manual: !!body.manual, ...(start ? { start } : {}) })
      .then(r => ({ ...r.data, _seq: seq }));
  }, {
    onSuccess: async (d: any, body) => {
      if (d.applied) {
        setPlan(null);
        // The save's answer is the day's clock until the page has asked again.
        setApplied({ ids: (d.stops || []).map((x: any) => x.job_id).join(','), data: d });
        if (body.shown) {
          toast.success(({ closeToast }) => (
            <span>Day in order. <button type="button" className="underline font-semibold"
              onClick={() => { undo.mutate({ date: d.date, before: d.before }); if (closeToast) closeToast(); }}>Undo</button></span>
          ), { autoClose: 10000 });
        } else if (!body.manual) toast.success('Day in order');
        else {
          // A drag saves without a word — unless it breaks a set time. He moved
          // a mow above the 12:00 pickup and three set times went late with
          // nothing said; he found out by scrolling. Said from the save's own
          // answer: only the times that were fine before this move.
          const lateNow: any[] = [...(d.late || []), ...(d.moved || [])];
          const was = body.lateBefore;
          const broke = was ? lateNow.filter(e => !was.includes(e.job_id)) : lateNow;
          if (broke.length) {
            const names = broke.map(e => e.customer_name).filter(Boolean).join(', ');
            toast.warn(was
              ? `That move makes ${broke.length === 1 ? '1 set time' : `${broke.length} set times`} late: ${names}.`
              : `In this order ${broke.length === 1 ? '1 set time is' : `${broke.length} set times are`} late: ${names}.`, { autoClose: 8000 });
          }
        }
        await Promise.all([qc.invalidateQueries('field-stops'), qc.invalidateQueries('bid-visits'), qc.invalidateQueries('day-clock')]);
        setDragOrder(null);
      } else if (d._seq === asked.current) setPlan(d);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not optimize the day'); },
  });
  // Start at changed (or "now" tapped): the plan is made again at once, so the
  // panel never shows one start in the box and another in the plan. A phone's
  // time wheel reports every notch it passes — wait for it to settle.
  const changeStart = (v: string) => {
    setStartAt(v);
    if (replanTimer.current) clearTimeout(replanTimer.current);
    replanTimer.current = setTimeout(() => { replanTimer.current = null; optimize.mutate({ start: v || null }); }, v ? 600 : 0);
  };
  // Drag to reorder: press and hold the grip on a phone, or drag with the mouse.
  // The dragged order is saved as is (manual) and put on the clock.
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchHold = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchFrom = useRef<number | null>(null);
  const touchOver = useRef<number | null>(null);
  // A finished stop stays where it is: dragged down the list it became "Stop 4 ·
  // Done" between two stops still to do and renumbered them.
  const canDrag = (j: any) => !readOnly && listsReady && j.status !== 'complete';
  // ── Dragging on a phone scrolls the page ───────────────────────────────────
  // Owner, Oct 5 2026: "I want the optimize and re route button to actually
  // work". Fourteen cards are three screens tall. The grip turns the browser's
  // own scrolling off (or the page would slide instead of the card), so a
  // stop held at the bottom could only ever reach the three rows on screen —
  // stop 13 could not be dragged above stop 11. While a stop is held and the
  // finger is near the top or bottom edge, the page scrolls itself, faster
  // the nearer the edge, and the row under the finger keeps up.
  const touchY = useRef<number | null>(null);
  const scrollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const rowUnder = (y: number) => {
    rowRefs.current.forEach((el, i) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (y >= r.top && y <= r.bottom && touchOver.current !== i) { touchOver.current = i; setDropIdx(i); }
    });
  };
  // What scrolls: the nearest box around the list that can, else the window.
  const scrollBox = (): HTMLElement | null => {
    let el: HTMLElement | null = rowRefs.current.find(Boolean)?.parentElement || null;
    for (; el && el !== document.body; el = el.parentElement) {
      const oy = window.getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
    }
    return null;
  };
  const stopScrolling = () => { if (scrollTimer.current) { clearInterval(scrollTimer.current); scrollTimer.current = null; } touchY.current = null; };
  const startScrolling = () => {
    if (scrollTimer.current) return;
    const box = scrollBox();
    const EDGE = 90;
    scrollTimer.current = setInterval(() => {
      const y = touchY.current;
      if (y === null || touchFrom.current === null) return;
      const rect = box ? box.getBoundingClientRect() : null;
      const top = Math.max(0, rect ? rect.top : 0), bottom = Math.min(window.innerHeight, rect ? rect.bottom : window.innerHeight);
      const by = y < top + EDGE ? -Math.min(18, 3 + (top + EDGE - y) / 5) : y > bottom - EDGE ? Math.min(18, 3 + (y - (bottom - EDGE)) / 5) : 0;
      if (!by) return;
      if (box) box.scrollTop += by; else window.scrollBy(0, by);
      rowUnder(y);
    }, 16);
  };
  useEffect(() => () => { if (scrollTimer.current) clearInterval(scrollTimer.current); }, []);
  function moveStop(from: number, to: number) {
    setDragIdx(null); setDropIdx(null);
    if (from === to || from < 0 || to < 0 || !listsReady) return;
    // One save at a time. A second drag while the first is still saving used
    // to race it — one of the two was lost. Wait for the list to come back.
    if (optimize.isLoading) { toast.info('Still saving the last change — move it again in a second'); return; }
    const ids = stops.map(j => j.id);
    const [m] = ids.splice(from, 1);
    ids.splice(to, 0, m);
    setDragOrder(ids);
    dropReplan();
    setPlan(null);
    // No start is sent: his order keeps the start it was saved with (the
    // server holds it). The Start at box belongs to the Optimize panel — a
    // time left in it by a plan he cancelled used to restart a dragged day.
    // lateBefore: which set times the clock already had late, so the answer
    // can say what THIS move broke.
    optimize.mutate({
      apply: true, manual: true, job_ids: ids, start: null,
      lateBefore: clock ? [...(clock.late || []), ...(clock.moved || [])].map((e: any) => e.job_id) : null,
    }, { onError: () => setDragOrder(null) });
  }
  function touchStart(idx: number, e: React.TouchEvent) {
    touchFrom.current = null;
    touchY.current = e.touches[0] ? e.touches[0].clientY : null;
    touchHold.current = setTimeout(() => {
      touchFrom.current = idx; touchOver.current = idx; setDragIdx(idx);
      if (navigator.vibrate) navigator.vibrate(30);
      startScrolling();
    }, 300);
  }
  function touchMove(e: React.TouchEvent) {
    const y = e.touches[0].clientY;
    touchY.current = y;
    if (touchFrom.current === null) return;
    rowUnder(y);
  }
  function touchEnd() {
    if (touchHold.current) { clearTimeout(touchHold.current); touchHold.current = null; }
    stopScrolling();
    const from = touchFrom.current, to = touchOver.current;
    touchFrom.current = null; touchOver.current = null;
    if (from !== null && to !== null) moveStop(from, to); else { setDragIdx(null); setDropIdx(null); }
  }

  const resetOrder = useMutation(() => api.delete('/jobs/day-order', { params: { date: day } }), {
    onSuccess: () => { toast.success('Back to time order'); qc.invalidateQueries('field-stops'); qc.invalidateQueries('bid-visits'); qc.invalidateQueries('day-clock'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not reset'); },
  });
  // Anytime, or a time it must start at — right on the stop.
  //
  // A quote visit's time goes through the visit's own route (the one its
  // sheet uses), so there is one writer for a visit's hours: an exact time or
  // "anytime" here replaces a "between" window instead of leaving it behind.
  // And every change refreshes the stops, the visits and the day's clock, and
  // closes an open plan — the card and the next Optimize both show the new
  // time without a reload (owner, Oct 5 2026: "mario has in between 7-10 but
  // stuck at 7" — the card kept the old time until the page was reloaded).
  //
  // On a saved day this used to SAVE the whole order again behind his back
  // (same order, re-timed from his house a quarter-hour from now, the answer
  // thrown away): he set one mow to 11:15, and a visit he had not touched
  // went red, the finished stops lost their times, and nothing said why. That
  // hidden save is gone. The lists reload, the clock is asked again because a
  // time in its key changed, and if the new time can't be kept where the stop
  // sits, the card, the paragraph above the list and the "Re-plan" line say so.
  const [timeFor, setTimeFor] = useState<string | null>(null);   // 'q12' a quote visit, 'j12' any other stop
  const [timeVal, setTimeVal] = useState('');
  const setTime = useMutation(({ id, time, quote }: { id: number; time: string | null; quote: boolean }) => (quote
    ? api.put(`/quotes/visits/${id}`, time ? { hours_mode: 'exact', visit_exact: time } : { hours_mode: 'any' })
    : api.post(`/jobs/${id}/time`, time ? { time_start: time, time_is_firm: true } : { time_start: null })), {
    onSuccess: async () => {
      setTimeFor(null); dropReplan(); setPlan(null);
      toast.success('Time saved');
      await Promise.all([qc.invalidateQueries('field-stops'), qc.invalidateQueries('bid-visits'), qc.invalidateQueries('day-clock'), qc.invalidateQueries('quote-visit')]);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save the time'); },
  });
  const timeKey = (j: any) => `${j._quote ? 'q' : 'j'}${j.id}`;
  const openTime = (j: any) => {
    if (timeFor === timeKey(j)) { setTimeFor(null); return; }
    setTimeFor(timeKey(j));
    setTimeVal(promiseOf(j).must_start || '');
  };
  // The box under a stop: a must-start time, or anytime.
  const timeBox = (j: any) => (timeFor !== timeKey(j) ? null : (
    <div className="mt-2 rounded-lg border border-gray-200 p-2.5 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-gray-600">Must start at</span>
        <input type="time" value={timeVal} onChange={e => setTimeVal(e.target.value)} className="input py-1 text-sm w-auto" />
        <button onClick={() => timeVal && setTime.mutate({ id: j.id, time: timeVal, quote: !!j._quote })} disabled={!timeVal || setTime.isLoading || readOnly}
          className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold disabled:opacity-40">Save</button>
      </div>
      <button onClick={() => setTime.mutate({ id: j.id, time: null, quote: !!j._quote })} disabled={setTime.isLoading || readOnly}
        className="w-full py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 disabled:opacity-50">Anytime {isToday ? 'today' : 'that day'} — no set time</button>
      {j._quote && <p className="text-[11px] text-gray-500">Between two times? <button type="button" onClick={() => navigate(`/quote-visits/${j.id}`)} className="underline font-semibold">Open the visit</button> and choose Between.</p>}
      <p className="text-[11px] text-gray-500">{j._quote ? 'This visit only.' : 'This stop only.'} Optimize keeps a must-start time and fits anytime stops around it.</p>
    </div>
  ));

  // A stop closed out while a plan preview is open: that plan was made with
  // the stop still to do, so it closes. The next Optimize leaves it out and
  // starts from it.
  const closedOut = () => { dropReplan(); setPlan(null); };
  const done = useMutation((id: number) => api.post(`/jobs/${id}/office-complete`), {
    onSuccess: () => { closedOut(); toast.success('Done — on the books'); qc.invalidateQueries('field-stops'); qc.invalidateQueries('jobs'); qc.invalidateQueries('day-clock'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark it done'); },
  });

  // ── Stops that start themselves when he gets there ─────────────────────────
  // Michael, Nov 3 2026: "Field / My Day auto-start jobs by location … zero taps required; crew tools
  // must add no more than ~30 s per stop." The rules are in utils/geoAutoStop (pure, tested) and the
  // browser half in hooks/useAutoStop; the owner's switch, radius and waits are in
  // Settings → Company → Start stops by location.
  //
  // Only today, only a real yard: a quote visit has its own On the way / Here / Done (and its own
  // texts), and a to-do is usually not at an address at all. A stop with no pin is skipped rather than
  // guessed at. `order` is the place in the day on screen, which is what "next in route order" means
  // here — it is the order he works.
  const geoStops: GeoStop[] = useMemo(() => stops
    .map((j, i) => ({ j, i }))
    .filter(({ j }) => !j._quote && j.service_type !== 'todo' && j.lat != null && j.lng != null
      && ['scheduled', 'in_progress'].includes(String(j.status || 'scheduled')))
    .map(({ j, i }) => ({
      id: j.id, lat: Number(j.lat), lng: Number(j.lng),
      status: j.status, actual_start_time: j.actual_start_time ?? null, order: i,
    })), [stops]);
  const auto = useAutoStop({
    stops: geoStops,
    enabled: isToday && !readOnly && listsReady && !!cm,
    onChanged: closedOut,
  });
  const nameOfStop = (jobId: number) => {
    const j = stops.find(x => x.id === jobId);
    return j ? (j.customer_name || stopAddr(j) || '') : '';
  };

  return (
    <div className="max-w-2xl space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Field</h1>
          <p className="text-xs text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · your quote visits, your mowing stops, the lawns to inspect</p>
        </div>
      )}

      {/* 1. The day's stops — mowing and quote visits together, by time; any day */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">{isToday ? "Today's stops" : 'Stops'}</h1>
            <p className="text-xs text-gray-500">{[
              open.length ? `${open.length} to do${quoteStops.length ? ` · ${quoteStops.length} quote visit${quoteStops.length === 1 ? '' : 's'}` : ''} · ${ordered ? 'your order' : 'by time'}`
                : stops.length ? `All done ${isToday ? 'for today' : 'that day'}`
                : `Nothing of your own ${isToday ? 'today' : 'that day'}`,
              // What the crew has on, so an empty list of his own never reads as an empty day.
              elsewhere ? `${elsewhere} more on the crew's day below` : '',
            ].filter(Boolean).join(' · ')}</p>
          </div>
          <div className="flex items-center gap-3">
            {open.length > 1 && listsReady && (
              <button onClick={() => optimize.mutate({})} disabled={optimize.isLoading} className="text-xs font-semibold text-purple-600 disabled:opacity-50">{optimize.isLoading && !plan ? 'Working…' : 'Optimize'}</button>
            )}
            <button onClick={() => setTodoOpen(o => !o)} className="text-xs font-semibold text-emerald-600">+ To-do</button>
            <button onClick={() => setVisitSheet({ date: day })} className="btn-primary text-xs">+ Add visit</button>
            <Link to="/routes" className="text-xs font-semibold text-brand-700">Routes</Link>
          </div>
        </div>
        <TodoSheet open={todoOpen} date={day} onClose={() => setTodoOpen(false)}
          onAdded={() => { setTodoOpen(false); qc.invalidateQueries('field-stops'); qc.invalidateQueries('jobs'); }} />
        {/* Any day: pick Monday and route Monday's quote visits (and mows) ahead of time. */}
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDay(-1)} aria-label="Day before" className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">‹</button>
          <label className="flex-1 relative">
            <span className={`block text-center py-2 rounded-lg border text-sm font-semibold ${isToday ? 'border-gray-300 text-gray-800' : 'border-purple-400 text-purple-700'}`}>{isToday ? `Today · ${dayWords}` : dayWords}</span>
            <input type="date" value={day} onChange={e => { if (e.target.value) goDay(e.target.value); }}
              className="absolute inset-0 opacity-0 cursor-pointer" aria-label="Pick a day" />
          </label>
          <button onClick={() => shiftDay(1)} aria-label="Next day" className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">›</button>
          {!isToday && <button onClick={() => goDay(today)} className="text-xs font-semibold text-brand-700">Today</button>}
        </div>
        {/* Things tapped with no signal, waiting on the phone — and a way to send them now. */}
        {(!isOnline || pending.length > 0) && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-center gap-2 flex-wrap">
            <WifiOff className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 min-w-[12rem]">
              {!isOnline
                ? `No signal. On a quote visit, On the way, Here, Done and Skip save on the phone and send when you are back online${pending.length ? ` — ${pending.length} waiting now` : ''}. Everything else on this page needs signal.`
                : `${pending.length} thing${pending.length === 1 ? '' : 's'} waiting to send.`}
            </span>
            {isOnline && pending.length > 0 && <button onClick={() => flush()} className="font-semibold underline">Send now</button>}
          </div>
        )}
        {isToday && !readOnly && !!cm && <AutoStartStrip auto={auto} nameOf={nameOfStop} />}
        {!cm && <p className="card p-4 text-sm text-gray-500">This login is not tied to an employee record, so no stop here is marked as yours{office ? " — the day is below, by route" : ' and only quote visits show'}. Employees → your card → link the login to see your mowing stops.</p>}
        {(cm || office) && isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {(cm || office) && isError && (
          <p className="text-sm text-red-600">
            Could not load your stops. <button onClick={() => stopsQ.refetch()} className="underline font-semibold">Try again</button>
          </p>
        )}
        {/* The last look failed but the one before is still here: say how old it is, keep everything working. */}
        {staleWords && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <span className="flex-1">No signal just now — showing the day as of {staleWords}.</span>
            <button onClick={lookAgain} className="font-semibold underline flex-shrink-0">Try again</button>
          </p>
        )}
        {/* The quote visits are their own list. If it did not load, say so — an empty-looking day is not an empty day. */}
        {visitsError && (
          <p className="text-sm text-red-600">
            Could not load your quote visits, so this list is not the whole day. <button onClick={() => refetchVisits()} className="underline font-semibold">Try again</button>
          </p>
        )}
        {plan && (
          <div className="card p-4 border-2 border-purple-300 space-y-2">
            {/* Where it leaves from (when that is not home) and when: "from Mario Delgado's · leave now". */}
            {/* yours: nothing better than the order he has was found, so this IS his order (server: hisOrderStands). */}
            <p className="text-sm font-semibold text-gray-900">
              {plan.yours ? 'Your order is already the best we can do' : 'Best order'} · {plan.from_job_id != null && startPlace(plan) ? `from ${startPlace(plan)} · ` : ''}{plan.leave_now ? 'leave now' : `leave ${clock12(plan.start)}`} · done about {clock12(plan.finish)} · {plan.drive_minutes} min driving
            </p>
            {plan.yours && <p className="text-xs text-gray-600">A new order would keep no more set times and end the day later, so this is the one you have, timed from now.</p>}
            {plan.start_passed && <p className="text-xs font-semibold text-amber-700">{clock12(plan.start_was)} has already gone by — planned from now.</p>}
            {plan.start_early && <p className="text-xs text-gray-600">This starts earlier than usual so a set time isn't missed.</p>}
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Start at</span>
              <input type="time" value={startAt} onChange={e => changeStart(e.target.value)} className="input py-1 text-sm w-auto" />
              <button onClick={() => optimize.mutate({})} disabled={optimize.isLoading} className="font-semibold text-purple-600 disabled:opacity-50">Re-plan</button>
              {startAt && <button onClick={() => changeStart('')} className="underline">now</button>}
            </div>
            <PlanKept plan={plan} />
            <ol className="text-sm space-y-1">
              {plan.stops.map((p: any, i: number) => (
                // A long time ("was 2–5 PM · now ~12:51 AM tomorrow") drops to its own
                // line on a phone instead of squeezing the name of the person he
                // has to call down to "Lena…".
                <li key={p.job_id} className="flex items-baseline gap-x-2 flex-wrap">
                  <span className="w-5 text-right text-gray-400 text-xs">{i + 1}</span>
                  <span className="flex-1 min-w-[9rem] truncate">{p.customer_name}{p.service_type === 'estimate' ? ' · quote' : ''}</span>
                  <span className="ml-auto"><PlanRowTime p={p} /></span>
                </li>
              ))}
            </ol>
            <PlanWaits plan={plan} />
            <PlanProblems plan={plan} isToday={isToday} dayWords={dayWords} me={user?.name || ''} />
            <PlanAfterEnd plan={plan} isToday={isToday} />
            {/* A to-do with no address ("Call the insurance agent") used to read "No GPS, put last" — and
                always went last. Now it is fitted into a wait when the day has one long enough; only the
                ones still on the END of the plan are said to go last. */}
            {(() => {
              const todo: any[] = (plan.stops || []).filter((p: any) => !p.fixed);
              const loose = (p: any) => !p.pinned && !p.set_time;
              let k = todo.length;
              while (k > 0 && loose(todo[k - 1])) k--;
              const last = todo.slice(k), fitted = todo.slice(0, k).filter(p => loose(p) && !p.address);
              const noAddress = last.filter(p => !p.address), noPin = last.filter(p => p.address);
              return (
                <>
                  {fitted.length > 0 && <p className="text-xs text-gray-500">No address, so {fitted.length === 1 ? 'it is' : 'they are'} fitted into a wait: {fitted.map(p => p.customer_name).join(', ')}</p>}
                  {noAddress.length > 0 && <p className="text-xs text-gray-500">No address, so {noAddress.length === 1 ? 'it goes' : 'they go'} last: {noAddress.map(p => p.customer_name).join(', ')}</p>}
                  {noPin.length > 0 && <p className="text-xs text-gray-500">Not found on the map yet, so {noPin.length === 1 ? 'it goes' : 'they go'} last: {noPin.map(p => p.customer_name).join(', ')}</p>}
                </>
              );
            })()}
            <PlanHelp />
            <div className="flex gap-2">
              <button onClick={() => { dropReplan(); optimize.mutate({ shown: plan }); }} disabled={optimize.isLoading} className="flex-1 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-semibold disabled:opacity-50">{optimize.isLoading ? 'Working…' : plan.yours ? 'Keep my order' : 'Use this order'}</button>
              <button onClick={() => { dropReplan(); setPlan(null); }} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700">Cancel</button>
            </div>
          </div>
        )}
        {/* A saved order, all day: when to leave, when he'll be done, what has
            changed since he saved it, and who needs a call — from the day's
            clock above. These used to exist only inside the Optimize preview;
            after "Use this order" the page said "your order" and nothing else. */}
        {ordered && !plan && clock?.finish && (
          <div className="card p-4 space-y-2">
            {/* Under way: the first stop still to do is the one he is on — "next: Priya Nair", not "leave now"
                (he may be half way there; the server's liveClock). */}
            <p className="text-sm font-semibold text-gray-900">
              Your order · {whereNow(clock)} · done about {clock12(clock.finish)} · {clock.drive_minutes} min driving
            </p>
            <PlanKept plan={clock} />
            <PlanWaits plan={clock} />
            {changedWords && (
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-center gap-3">
                <span className="flex-1">{changedWords}</span>
                <button onClick={() => optimize.mutate({})} disabled={optimize.isLoading || !listsReady} className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-semibold disabled:opacity-50">Re-plan</button>
              </p>
            )}
            <PlanAfterEnd plan={clock} isToday={isToday} />
            <PlanProblems plan={clock} isToday={isToday} dayWords={dayWords} me={user?.name || ''} saved />
          </div>
        )}
        {ordered && !plan && (
          <p className="text-xs text-gray-500">In your order — drag ⠿ to move a stop. <button
            onClick={() => { if (window.confirm(`Clear your order for ${isToday ? 'today' : dayWords} and list the stops by time again?`)) resetOrder.mutate(); }}
            disabled={resetOrder.isLoading} className="underline">Back to time order</button></p>
        )}
        {mapStops.length > 0 && unpinned < mapStops.length && (
          <div className="card overflow-hidden p-0">
            <RouteMap stops={mapStops} currentId={nextId} gps={null} />
            {unpinned > 0 && <p className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100">{unpinned} stop{unpinned === 1 ? ' is' : 's are'} not on the map — no address, or the map has not found {unpinned === 1 ? 'it' : 'them'} yet.</p>}
          </div>
        )}
        {!ordered && clock?.finish && (
          <p className="text-xs text-gray-500 px-1">
            On the clock as listed: {whereNow(clock)} · done about {clock12(clock.finish)} · {clock.drive_minutes} min driving. Optimize or drag to change the order.
          </p>
        )}
        {!ordered && !plan && <PlanAfterEnd plan={clock} isToday={isToday} />}
        {stops.map((j, i) => (
          <div key={j._quote ? `q${j.id}` : j.id} ref={el => { rowRefs.current[i] = el; }}
            draggable={canDrag(j)}
            onDragStart={() => setDragIdx(i)}
            onDragOver={e => { e.preventDefault(); setDropIdx(i); }}
            onDrop={() => dragIdx !== null && moveStop(dragIdx, i)}
            onDragEnd={() => { setDragIdx(null); setDropIdx(null); }}
            className={`flex items-stretch gap-1 rounded-xl ${dragIdx === i ? 'opacity-40' : ''} ${dropIdx === i && dragIdx !== i ? 'ring-2 ring-purple-400' : ''}`}>
            {canDrag(j) ? (
              <span title="Press and hold, then drag to move this stop"
                className="flex items-center px-0.5 text-gray-400 cursor-grab select-none"
                style={{ touchAction: 'none', WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as React.CSSProperties}
                onTouchStart={e => touchStart(i, e)} onTouchMove={touchMove} onTouchEnd={touchEnd} onTouchCancel={touchEnd}>
                <GripVertical className="h-5 w-5" />
              </span>
            ) : !readOnly && listsReady ? <span className="w-6 flex-shrink-0" aria-hidden="true" /> : null}
            <div className="flex-1 min-w-0">
        {j._quote ? (
          <QuoteVisitCard key={`q${j.id}`} v={j} stopText={`Stop ${i + 1}`} onIntake={setIntake} timeNote={whenNote(j)}
            onTime={readOnly ? undefined : () => openTime(j)} below={timeBox(j)} onChanged={closedOut}
            tag={<span className="font-semibold text-purple-500">{String(j.title || '').startsWith('Lawn walkthrough') ? 'Lawn walkthrough' : 'Quote visit'}</span>} />
        ) : (
          <div key={j.id} className={`card p-4 ${j.status === 'complete' ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Stop {i + 1} · {whenNote(j) || ownTime(j)} · {label(j)}</p>
                <p className="font-semibold text-gray-900 truncate">{j.service_type === 'todo' ? <><span className="text-emerald-600">✓</span> {j.title || 'To-do'}{j.todo_customer_name ? <span className="font-normal text-gray-600"> · {j.todo_customer_name}</span> : null}</> : j.customer_name}</p>
                {stopAddr(j) && <p className="text-sm text-gray-600 truncate">{stopAddr(j)}{j.city ? `, ${j.city}` : ''}</p>}
                {j.service_type === 'todo' && j.notes && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{j.notes}</p>}
                {(j.property_crew_notes || j.gate_notes) && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{[j.gate_notes, j.property_crew_notes].filter(Boolean).join(' · ')}</p>}
              </div>
              {stopAddr(j) && (
                <a href={mapsLink(j)} target="_blank" rel="noreferrer" className="flex-shrink-0 p-2 rounded-lg bg-brand-50 text-brand-700"><Navigation className="h-5 w-5" /></a>
              )}
            </div>
            {j.status !== 'complete' ? (
              <div className="flex gap-2 mt-3">
                <button onClick={() => navigate(`/jobs/${j.id}`)} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700">Open</button>
                <button onClick={() => openTime(j)}
                  className="px-3 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 flex items-center gap-1"><Clock className="h-4 w-4" /> Time</button>
                <button onClick={() => { if (window.confirm(`Mark ${j.service_type === 'todo' ? (j.title || 'this to-do') : j.customer_name} done?`)) done.mutate(j.id); }} disabled={done.isLoading}
                  className="flex-1 py-2.5 rounded-lg bg-green-600 text-white text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"><CheckCircle className="h-4 w-4" /> Done</button>
              </div>
            ) : (
              <p className="text-xs text-green-700 mt-2 flex items-center gap-1"><Check className="h-3.5 w-3.5" /> Done</p>
            )}
            {timeBox(j)}
          </div>
        )}
            </div>
          </div>
        ))}
      </section>

      {/* 1b. The rest of the day — the stops somebody else is on, by route.
          Owner's Field page used to show nothing at all on a day his crew is
          out: the list only ever held the stops HE is on (server/routes/jobs.js
          ?crew_member_id=), and Monday Week B is Ryan and James's. The day's
          whole work is here now, grouped and read only; his own stops above
          are still the only ones he drags, Optimizes and is timed on. */}
      {dayGroups.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">The rest of the day</h2>
            <p className="text-xs text-gray-500 flex-shrink-0">{elsewhere} stop{elsewhere === 1 ? '' : 's'} · {elsewhereOpen ? `${elsewhereOpen} to do` : 'all done'}</p>
          </div>
          <p className="text-xs text-gray-500">Not yours to drive — open a stop to change it, or the <Link to="/routes" className="underline font-semibold">Routes page</Link> to reorder a day.</p>
          {dayGroups.map(g => <DayGroupCard key={g.key} g={g} />)}
        </section>
      )}

      {/* 2. Lawns to inspect — the inspector's week, this week only */}
      <section><InspectorHome embedded /></section>

      {visitSheet && <VisitSheet init={visitSheet} onClose={() => setVisitSheet(null)} />}
      {intake && <IntakeSheet visit={intake} onClose={() => setIntake(null)} onQuoted={(qid) => { setIntake(null); navigate('/quotes', { state: { openQuoteId: qid } }); }} />}
    </div>
  );
}
